import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";

type ApacheModule = {
  id: string;
  name: string;
  shortName: string;
  enabled: boolean;
  available?: boolean;
};

/**
 * Softaculous/Webuzo-style Apache Modules manager.
 */
export function ApacheModulesPage() {
  const [items, setItems] = useState<ApacheModule[]>([]);
  const [enabledCount, setEnabledCount] = useState(0);
  const [disabledCount, setDisabledCount] = useState(0);
  const [note, setNote] = useState("");
  const [hasApache, setHasApache] = useState(false);
  const [filter, setFilter] = useState<"all" | "enabled" | "disabled">("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [showNote, setShowNote] = useState(true);
  const [showCaution, setShowCaution] = useState(true);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api<{
        items: ApacheModule[];
        enabledCount: number;
        disabledCount: number;
        note?: string;
        hasApache?: boolean;
      }>("/api/v1/server-config/apache-modules");
      setItems(res.items || []);
      setEnabledCount(res.enabledCount ?? 0);
      setDisabledCount(res.disabledCount ?? 0);
      setNote(res.note || "");
      setHasApache(Boolean(res.hasApache));
      setPending({});
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const display = useMemo(() => {
    let list = items.map((m) => ({
      ...m,
      enabled: pending[m.id] !== undefined ? pending[m.id] : m.enabled
    }));
    if (filter === "enabled") list = list.filter((m) => m.enabled);
    if (filter === "disabled") list = list.filter((m) => !m.enabled);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.shortName.toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, filter, search, pending]);

  const liveEnabled = display.filter((m) => m.enabled).length;
  const liveDisabled = display.filter((m) => !m.enabled).length;
  const allSelected =
    display.length > 0 && display.every((m) => m.enabled);

  const toggleOne = (id: string, enabled: boolean) => {
    setPending((p) => ({ ...p, [id]: enabled }));
  };

  const selectAll = (enabled: boolean) => {
    const next: Record<string, boolean> = { ...pending };
    for (const m of display) next[m.id] = enabled;
    setPending(next);
  };

  const applyChanges = async () => {
    const modules = Object.entries(pending).map(([id, enabled]) => ({
      id,
      enabled
    }));
    if (!modules.length) {
      setMsg("No changes to save");
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/server-config/apache-modules", {
        method: "PUT",
        body: JSON.stringify({ modules })
      });
      setMsg(`Updated ${modules.length} module(s)`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const resetModules = async () => {
    if (
      !confirm(
        "Reset all Apache modules to recommended defaults? This may reload Apache."
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/server-config/apache-modules", {
        method: "PUT",
        body: JSON.stringify({ reset: true, modules: [] })
      });
      setMsg("Modules reset to defaults");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const dirty = Object.keys(pending).length > 0;

  return (
    <>
      <div className="topbar">
        <div>
          <h1 className="apache-mod-title">
            <span className="apache-mod-icon" aria-hidden>
              🪶
            </span>
            Apache Modules
          </h1>
          <p>Enable or disable Apache modules (Softaculous-style)</p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn apache-mod-reset"
            disabled={busy}
            onClick={() => void resetModules()}
          >
            Reset Module
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="apache-mod-toolbar">
        <label className="apache-mod-search-label">
          Search Module :
          <select
            value={filter}
            onChange={(e) =>
              setFilter(e.target.value as "all" | "enabled" | "disabled")
            }
          >
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </label>
        <input
          type="search"
          className="apache-mod-search-input"
          placeholder="Filter by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {showNote ? (
        <div className="apache-mod-banner apache-mod-note">
          <div>
            <strong>Note:</strong> Some modules depend on other modules, which
            means they can only be turned off (or on) when the modules they
            depend on are also turned off (or on).
          </div>
          <button
            type="button"
            className="apache-mod-dismiss"
            onClick={() => setShowNote(false)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ) : null}

      {showCaution ? (
        <div className="apache-mod-banner apache-mod-caution">
          <div>
            <strong>Caution:</strong> Toggling modules may impact Apache
            performance or even render it inoperable. Proceed only if you fully
            understand the consequences of your actions.
          </div>
          <button
            type="button"
            className="apache-mod-dismiss"
            onClick={() => setShowCaution(false)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ) : null}

      {!hasApache ? (
        <div className="card full" style={{ marginBottom: 12 }}>
          <p className="card-meta faint">
            {note ||
              "Apache not detected on this host — preferences are stored and applied when Apache is available (a2enmod)."}
          </p>
        </div>
      ) : null}

      <div className="apache-mod-panel">
        <div className="apache-mod-panel-head">
          <span>
            Modules [ Enabled: {dirty ? liveEnabled : enabledCount}, Disabled:{" "}
            {dirty ? liveDisabled : disabledCount} ]
          </span>
          <label className="apache-mod-select-all">
            Select All
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => selectAll(e.target.checked)}
            />
          </label>
        </div>

        <div className="apache-mod-grid">
          {display.length === 0 ? (
            <div className="apache-mod-empty">No modules match the filter.</div>
          ) : (
            display.map((m) => (
              <label key={m.id} className="apache-mod-item">
                <input
                  type="checkbox"
                  checked={m.enabled}
                  onChange={(e) => toggleOne(m.id, e.target.checked)}
                />
                <span className="mono">{m.name}</span>
              </label>
            ))
          )}
        </div>

        <div className="apache-mod-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !dirty}
            onClick={() => void applyChanges()}
          >
            {busy ? "Saving…" : "Save Settings"}
          </button>
          {dirty ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setPending({})}
            >
              Discard changes
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
